/**
 * Statistics page constants
 */

// Service execution order in pipeline
export const SERVICE_ORDER = ["merovingian", "sati", "sentinel", "seraph"];

// Pipeline services (core processing flow)
export const PIPELINE_SERVICES = ["merovingian", "sati", "sentinel", "seraph"];

// Support services (orchestration, not in main processing flow)
export const SUPPORT_SERVICES = ["source", "trainman", "oracle", "archivist"];

export const SERVICE_LABELS = {
  merovingian: "Merovingian",
  sati: "Sati",
  sentinel: "Sentinel",
  seraph: "Seraph",
  source: "Source",
  trainman: "Trainman",
  oracle: "Oracle",
  archivist: "Archivist",
};

export const SERVICE_DESCRIPTIONS = {
  merovingian: "Ingest & Queue",
  sati: "Image Processing",
  sentinel: "Detection & Crop",
  seraph: "Face Recognition",
  source: "FTP Upload",
  trainman: "Training Processing",
  oracle: "AI Services",
  archivist: "EXIF & Archive",
};

export const SERVICE_COLORS = {
  merovingian: "#ff9b64",
  sati: "#66ffb7",
  sentinel: "#8ce5ff",
  seraph: "#a29bff",
  source: "#ff7eb3",
  trainman: "#b8e986",
  oracle: "#ff6b6b",
  archivist: "#ffd666",
};

// Map service names to their queue names (queues don't follow ${service}_jobs pattern)
export const QUEUE_MAP = {
  merovingian: null,           // Merovingian is the producer, not consumer
  sati: "scan_jobs",           // Merovingian -> Sati
  sentinel: "edited_jobs",     // Sati -> Sentinel
  seraph: "detection_jobs",    // Sentinel -> Seraph
  trainman: null,              // Trainman produces training_jobs
};

// Step labels for display (API provides structure via stats.steps)
export const STEP_LABELS = {
  // Merovingian
  stability_check: "Stability Check",
  format_validation: "Format Validation",
  duplicate_check: "Duplicate Check",
  registration: "Registration",
  // Sati
  load: "Load",
  pp3_apply: "PP3 Processing",
  orientation: "Orientation",
  preview_gen: "Preview",
  detect_gen: "Detection Image",
  thumb_gen: "Thumbnail",
  metadata_extract: "Metadata",
  metadata_save: "Metadata Save",
  // Sentinel
  exif: "EXIF Read",
  roll: "Roll Correction",
  detection: "YOLO Detection",
  classification: "Classification",
  scoring: "Subject Scoring",
  selection: "Selection",
  cropping: "Crop",
  quality: "Quality",
  visualization: "Visualization",
  edit: "Edit Output",
  // Seraph
  face_detect: "Face Detection",
  face_filter: "Face Filter",
  embedding: "Embedding",
  match: "Face Match",
  crop_gen: "Face Crops",
  viz_update: "Viz Update",
  persist: "Persist",
};

// Crop learning feature labels (user-friendly names and descriptions)
export const FEATURE_LABELS = {
  face_size: { label: "Face Size", desc: "How large faces appear in the final crop" },
  face_size_ratio: { label: "Face/Frame Ratio", desc: "Face area relative to total crop area" },
  headroom: { label: "Headroom", desc: "Space above subject's head" },
  headroom_ratio: { label: "Headroom %", desc: "Headroom as percentage of frame height" },
  center_offset_x: { label: "Horizontal Offset", desc: "How far subject is from horizontal center" },
  center_offset_y: { label: "Vertical Offset", desc: "How far subject is from vertical center" },
  rule_of_thirds_score: { label: "Rule of Thirds", desc: "Alignment with compositional guidelines" },
  crop_tightness: { label: "Crop Tightness", desc: "How closely crop hugs the subject" },
  aspect_ratio: { label: "Aspect Ratio", desc: "Width-to-height ratio of crop" },
  subject_coverage: { label: "Subject Coverage", desc: "How much of crop is filled by subject" },
  edge_distance: { label: "Edge Clearance", desc: "Minimum distance from crop edges" },
  delta_face_size: { label: "Face Size Change", desc: "Your typical face size adjustments" },
  delta_headroom: { label: "Headroom Change", desc: "Your typical headroom adjustments" },
  delta_center_x: { label: "Horizontal Shift", desc: "Your typical left/right adjustments" },
  delta_center_y: { label: "Vertical Shift", desc: "Your typical up/down adjustments" },
};

// Editor send reason labels
export const EDITOR_SEND_REASON_LABELS = {
  crop_unsatisfactory: "Crop unsatisfactory",
  complex_edit_needed: "Complex edit needed",
  special_request: "Special request",
  ai_detection_failed: "AI detection failed",
  color_correction: "Color correction",
  other: "Other",
};

// Date filter presets
export const DATE_PRESETS = [
  { label: "All Time", from: null, to: null },
  { label: "Today", getDates: () => {
    const today = new Date().toISOString().split("T")[0];
    return { from: today, to: today };
  }},
  { label: "Yesterday", getDates: () => {
    const yesterday = new Date(Date.now() - 86400000).toISOString().split("T")[0];
    return { from: yesterday, to: yesterday };
  }},
  { label: "Last 7 Days", getDates: () => ({
    from: new Date(Date.now() - 7 * 86400000).toISOString().split("T")[0],
    to: null
  })},
  { label: "Last 30 Days", getDates: () => ({
    from: new Date(Date.now() - 30 * 86400000).toISOString().split("T")[0],
    to: null
  })},
];

// Statistics tabs
export const STATS_TABS = [
  { id: "pipeline", label: "Performance" },
  { id: "timeline", label: "Timeline" },
  { id: "camera", label: "Camera" },
  { id: "archive", label: "Archive" },
  { id: "detection", label: "Detection" },
  { id: "editor_sends", label: "Editor Sends" },
  { id: "model_quality", label: "AI Quality" },
  { id: "training", label: "Training" },
  { id: "system", label: "System" },
];

// Refresh intervals (ms)
export const REFRESH_INTERVALS = {
  stats: 60000,      // 1 minute
  archive: 5000,     // 5 seconds
  training: 10000,   // 10 seconds
  system: 5000,      // 5 seconds
  queues: 5000,      // 5 seconds
  alerts: 10000,     // 10 seconds
  cluster: 5000,     // 5 seconds
};

// =============================================================================
// System Dashboard Constants
// =============================================================================

// Machine roles
export const MACHINE_ROLES = {
  'cpu-server': { label: 'CPU Server', color: '#66ffb7' },
  'gpu-farm': { label: 'GPU Farm', color: '#a29bff' },
  'storage': { label: 'Storage', color: '#ffd666' },
  'hybrid': { label: 'Hybrid', color: '#8ce5ff' },
};

// Queue names and labels
export const QUEUE_LABELS = {
  scan_jobs: { label: 'Scan Jobs', desc: 'Merovingian -> Sati' },
  edited_jobs: { label: 'Edited Jobs', desc: 'Sati -> Sentinel' },
  'sentinel:recrop': { label: 'Recrop', desc: 'Priority recrops' },
  'sentinel:edit': { label: 'Edit Gen', desc: 'Edit generation' },
  'sentinel:viz': { label: 'Viz Gen', desc: 'Visualization generation' },
  'sentinel:dlq': { label: 'Dead Letter', desc: 'Failed jobs' },
  detection_jobs: { label: 'Detection', desc: 'Sentinel -> Seraph' },
  training_jobs: { label: 'Training', desc: 'Training images' },
  export_jobs: { label: 'Export', desc: 'FTP export jobs' },
};

// Queue health thresholds
export const QUEUE_THRESHOLDS = {
  warning: 50,    // Show warning if depth > 50
  critical: 100,  // Show critical if depth > 100
  stale: 300,     // Job is stale if > 5 minutes old
};

// Alert severity colors
export const ALERT_SEVERITY_COLORS = {
  info: '#8fbba8',      // Muted green
  warning: '#ffd666',   // Amber
  critical: '#ff6b6b',  // Red
};

// GPU temperature thresholds (Celsius)
export const GPU_TEMP_THRESHOLDS = {
  normal: 60,     // Green: < 60C
  warm: 75,       // Amber: 60-75C
  hot: 85,        // Orange: 75-85C
  critical: 85,   // Red: > 85C
};

// Status colors for the dashboard
export const STATUS_COLORS = {
  healthy: '#66ffb7',    // Green
  warning: '#ffd666',    // Amber
  critical: '#ff6b6b',   // Red
  unknown: '#8fbba8',    // Muted
  offline: '#555555',    // Gray
};

// Container status mapping
export const CONTAINER_STATUS_COLORS = {
  running: '#66ffb7',
  exited: '#ff6b6b',
  paused: '#ffd666',
  restarting: '#8ce5ff',
  dead: '#ff6b6b',
};

// Format uptime from seconds to human-readable string
export function formatUptime(seconds) {
  if (!seconds || seconds < 0) return '-';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return mins ? `${hours}h ${mins}m` : `${hours}h`;
  }
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  return hours ? `${days}d ${hours}h` : `${days}d`;
}

// Format bytes to human-readable string
export function formatBytes(bytes, decimals = 1) {
  if (bytes === 0) return '0 B';
  if (!bytes) return '-';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i];
}

// Get color for GPU temperature
export function getGpuTempColor(temp) {
  if (temp < GPU_TEMP_THRESHOLDS.normal) return STATUS_COLORS.healthy;
  if (temp < GPU_TEMP_THRESHOLDS.warm) return STATUS_COLORS.warning;
  if (temp < GPU_TEMP_THRESHOLDS.hot) return '#ff9b64'; // Orange
  return STATUS_COLORS.critical;
}

// Get color for queue health
export function getQueueHealthColor(depth, oldestAge) {
  if (depth > QUEUE_THRESHOLDS.critical) return STATUS_COLORS.critical;
  if (depth > QUEUE_THRESHOLDS.warning) return STATUS_COLORS.warning;
  if (oldestAge && oldestAge > QUEUE_THRESHOLDS.stale) return STATUS_COLORS.warning;
  return STATUS_COLORS.healthy;
}
